import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { track, trackLeadGenerated, trackBookCounselling } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { UserCheck, Loader2, Check, CalendarClock } from "lucide-react";

const COURSES = ["B.Tech", "Computer Engineering", "MBA", "BBA", "B.Com", "B.Sc", "B.A", "MBBS", "BDS", "BA LLB", "BCA"];
const TIMES = ["Morning (9 AM - 12 PM)", "Afternoon (12 PM - 4 PM)", "Evening (4 PM - 8 PM)"];

export default function ExpertCounseling() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", city: "Pune", courseInterest: "", budget: "", preferredDate: "", preferredTime: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile.trim()) || !form.courseInterest) {
      toast.error("Please fill name, valid mobile and course interest.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads/expert-counseling", { ...form, budget: form.budget ? Number(form.budget) : null, attribution: getAttribution() });
      track("form_submit", { form: "expert_counseling", course: form.courseInterest, city: form.city });
      trackLeadGenerated(data, {
        intent: "expert_counseling",
        course: form.courseInterest,
        city: form.city,
        lead_type: "expert_counseling_form",
        form_name: "Expert Counselling",
      });
      // Fire the canonical booking event ONCE per confirmed backend booking. The lead
      // id acts as the dedupe key across double-clicks, remounts and navigations.
      if (data?.id) trackBookCounselling(data.id, { booking_type: "expert_counselling", course: form.courseInterest });
      navigate("/thank-you", { state: { kind: "expert" } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="expert-counseling-page">
      <SEOHead title="Book Expert Counseling" description="Book a one-on-one session with a CollegeUnlock admission expert." />
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FDF9F1] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#C08E3B]">
            <UserCheck className="h-3.5 w-3.5" /> 1-on-1 Guidance
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Book Expert Counseling</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Get personalized admission guidance from an experienced counselor who knows Pune colleges inside out.
          </p>
          <ul className="mt-6 space-y-3">
            {["One-on-One Admission Expert", "Personalized College Selection", "Career Guidance", "Admission Strategy"].map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0B2A6B]"><Check className="h-3 w-3 text-white" /></span>
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-2xl bg-[#0B2A6B] p-5 text-white flex items-center gap-3">
            <CalendarClock className="h-6 w-6 text-[#D4A24C]" />
            <p className="text-sm">Our team will call you to confirm your preferred slot. No payment needed.</p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-4">
          <Field label="Student Name *"><input data-testid="expert-input-name" className="cu-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" /></Field>
          <Field label="Mobile Number *"><input data-testid="expert-input-mobile" className="cu-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" /></Field>
          <Field label="City"><input data-testid="expert-input-city" className="cu-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Course Interest *">
            <select data-testid="expert-input-course" className="cu-input" value={form.courseInterest} onChange={(e) => set("courseInterest", e.target.value)}>
              <option value="">Select a course</option>
              {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Budget (₹ per year)"><input data-testid="expert-input-budget" type="number" className="cu-input" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. 200000" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred Date"><input data-testid="expert-input-date" type="date" className="cu-input" value={form.preferredDate} onChange={(e) => set("preferredDate", e.target.value)} /></Field>
            <Field label="Preferred Time">
              <select data-testid="expert-input-time" className="cu-input" value={form.preferredTime} onChange={(e) => set("preferredTime", e.target.value)}>
                <option value="">Select</option>
                {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <button data-testid="expert-submit-btn" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A24C] px-6 py-3 text-sm font-semibold text-white hover:bg-[#C08E3B] disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><UserCheck className="h-4 w-4" /> Book My Session</>}
          </button>
        </form>
      </div>
      <style>{`.cu-input{display:flex;height:3rem;width:100%;border-radius:0.5rem;border:1px solid #cbd5e1;background:#fff;padding:0 1rem;font-size:0.875rem;outline:none;transition:all .15s}.cu-input:focus{border-color:transparent;box-shadow:0 0 0 2px #0B2A6B}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
